import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageFilterGroupType } from '@common/paged-data/enums/page-filter-group-type.enum';
import { PageFilterOperator } from '@common/paged-data/enums/page-filter-operator.enum';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { LogStreamMessage, LogStreamService } from '@portal-core/live-log/services/log-stream.service';
import { LogPageFilterConfig, LogTypeFilter, logTypeFilterIdToLogMessageType, logTypeFilterLabel } from '@portal-core/live-log/util/log-page-filter-config';
import { extractStringFilterValues, extractTypeFilterValues, extractTypeFilterValuesFromGroup, logMessageTypeColorClass as resolveLogMessageTypeColorClass, logMessageTypeIcon as resolveLogMessageTypeIcon, makeTypeFilter, parseEventToLogStreamRow, parseLogToLogStreamRow, TYPE_COLUMN_NAME } from '@portal-core/live-log/util/log-stream-grid';
import { LogDialogComponent } from '@portal-core/logs/components/log-dialog/log-dialog.component';
import { ProcessLogMessageType } from '@portal-core/processes/enums/process-log-message-type.enum';
import { LogStreamRow } from '@portal-core/processes/models/log-stream-row.model';
import { LogStreamRowService } from '@portal-core/processes/services/log-stream-row.service';
import { GridFilterName } from '@portal-core/ui/grid/enums/grid-filter-name.enum';
import { DataGridBase } from '@portal-core/ui/grid/util/data-grid-base';
import { DataGridControl } from '@portal-core/ui/grid/util/data-grid-control';
import { UserSettings } from '@portal-core/users/interfaces/user-settings';
import { USER_SETTINGS_SERVICE } from '@portal-core/users/tokens/user-settings-service.token';
import { asyncScheduler, of, ReplaySubject, Subscription } from 'rxjs';
import { map, scan, skip, tap, throttleTime } from 'rxjs/operators';

// Caller-supplied resolver that maps a row's File annotation onto a router-link command array
// (or null to render the file as plain text). Each feature using the grid knows how to address
// its own files (build → license/project/branch path, publication → the associated build's
// project/branch, …) so the component itself stays neutral.
export type LogStreamGridSourceLinkResolver = (src: string, line?: number) => any[] | null;

@Component({
  selector: 'mc-log-stream-grid',
  templateUrl: './log-stream-grid.component.html',
  styleUrls: ['./log-stream-grid.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LogStreamGridComponent extends DataGridBase<LogStreamRow> implements OnChanges, OnDestroy, OnInit {
  // The SSE endpoint to stream from. The owning component (build-profile-dialog,
  // publication-profile-dialog, …) resolves it via its feature-specific API service and hands
  // it in. Re-binding to a different URL tears down the existing subscription and re-opens.
  @Input() streamUrl: string;

  // Persistent storage key for the user's Type column filter selection. Per-feature so each
  // grid keeps its own selection (e.g. `build-logs-grid:type-filter`).
  @Input() settingsKey: string;

  // Tail of the empty-grid label — rendered as `There are no log entries for ${emptySubject}.`
  // Examples: "this build" / "this publication".
  @Input() emptySubject: string = 'this stream';

  // Optional caller-provided resolver for source-file router links. When omitted (or it
  // returns null), the File column renders the path as plain text.
  @Input() sourceLinkResolver?: LogStreamGridSourceLinkResolver;

  // Optional: caller-supplied empty-state message that supersedes the default. The build /
  // publication profile dialogs use this to surface "No log is available for this build." when
  // no artifacts exist on disk.
  @Input() emptyLabelOverride?: string;

  // When true, an indeterminate progress bar replaces the empty-state text. Used while the
  // prepare endpoint is polling (Redis empty, snapshot being built from a legacy `.mclog`).
  @Input() loading: boolean = false;

  // When set, an error panel is rendered in place of the loader / empty state. Caller passes
  // the values from a `LoadingState`'s `generalError$` / `detailedErrors$` observables.
  @Input() generalError?: string;
  @Input() detailedErrors?: string[];

  // Flips true when the SSE stream emits its terminal `done` event.
  streamComplete: boolean = false;

  get emptyLabel(): string {
    if (this.emptyLabelOverride) {
      return this.emptyLabelOverride;
    }
    if (!this.streamComplete) {
      return 'Waiting for output...';
    }
    return `There are no log entries for ${this.emptySubject}.`;
  }

  filterConfig: LogPageFilterConfig = new LogPageFilterConfig();

  // The data grid owns the subscription to this Observable, started when its `loadItems` flow
  // first runs and replaced on `hardReload` (which fires when the grid's filter state changes
  // or `streamUrl` changes).
  //
  // The fetcher reads from `this.entries$` — a ReplaySubject(1) driven by a class-level
  // scan subscription managed in ngOnChanges / ngOnDestroy. Filter/page changes re-subscribe
  // to the ReplaySubject and get the latest snapshot immediately without replaying all SSE
  // messages through scan. `throttleTime` caps re-renders to ~7/second during active streaming.
  gridControl: DataGridControl<LogStreamRow> = new DataGridControl(this.logStreamRowService, (pageFilter: PageFilter) => {
    if (!this.streamUrl) {
      return of(null);
    }
    const allowedTypes = this.extractTypeFilter(pageFilter);
    const stringFilters = extractStringFilterValues(pageFilter);
    // Capture the requested page slice from the filter — the data-grid re-invokes the fetcher
    // each time the user navigates to a new page, with `PageNumber` / `PerPage` updated. We
    // slice the accumulator client-side to match.
    const pageNumber = pageFilter?.PageNumber ?? 0;
    const pageSize = pageFilter?.PerPage ?? 25;
    return this.entries$.pipe(
      // Cap grid re-renders to ~7/second during active streaming. `trailing: true` ensures the
      // final accumulated state always flushes before the stream completes.
      throttleTime(150, asyncScheduler, { leading: true, trailing: true }),
      map(entries => {
        const visible = entries.filter(entry => {
          if (allowedTypes !== null && !allowedTypes.has(entry.LogMessageType)) return false;
          for (const [col, term] of stringFilters) {
            const field = (() => {
              switch (col) {
                case 'Message': return entry.Message;
                case 'ErrorId': return entry.ErrorId != null ? String(entry.ErrorId) : null;
                case 'File': return entry.File;
                case 'Subsystem': return entry.Subsystem;
                default: return null;
              }
            })();
            if (!field?.toLowerCase().includes(term)) return false;
          }
          return true;
        });
        const start = pageNumber * pageSize;
        const pageItems = visible.slice(start, start + pageSize);
        return {
          // `AllPagesLoaded: true` — the in-memory accumulator is the source of truth, no
          // server round-trip would surface more pages. The data-grid uses this signal to
          // decide whether to derive Total from the page count when Total is -1.
          AllPagesLoaded: true,
          Items: pageItems,
          Page: pageNumber,
          PropertyNameErrors: [],
          Total: visible.length
        } as Page<LogStreamRow>;
      }),
      tap({
        complete: () => {
          this.streamComplete = true;
          // OnPush: the empty-grid template binds `{{ emptyLabel }}` via a getter; without an
          // explicit markForCheck the swapped label wouldn't repaint.
          this.changeDetectorRef.markForCheck();
        }
      })
    );
  }, this.filterConfig);

  private filtersSubscription: Subscription;

  // ReplaySubject(1) holding all log entries accumulated from the SSE stream for the current
  // streamUrl. Driven by streamSub; torn down and rebuilt in resetEntries() on each streamUrl
  // change. The fetcher subscribes here directly — filter/page changes (hardReload) get the
  // latest snapshot without re-scanning all SSE messages. Completion is forwarded from the
  // underlying stream so the fetcher's `tap({ complete })` fires correctly.
  private entries$ = new ReplaySubject<LogStreamRow[]>(1);
  private streamSub: Subscription | null = null;

  constructor(
    private dialog: MatDialog,
    private logStreamService: LogStreamService,
    private logStreamRowService: LogStreamRowService,
    private changeDetectorRef: ChangeDetectorRef,
    @Optional() @Inject(USER_SETTINGS_SERVICE) private userSettingsService: UserSettings | null
  ) {
    super();
  }

  ngOnInit() {
    // Restore the user's saved Type-filter selection. The key check is "is the setting
    // defined?" — not "does the NGXS state already have a filter?". The data-grid's "Clear"
    // button wipes filter state to undefined, but the user's intent ("show everything") is
    // captured by storing an empty array. So:
    //   - undefined (never opened this grid before): seed the default Error+Warning, persist.
    //   - [] (cleared): apply no filter — grid shows everything.
    //   - [...]: apply those values.
    const stored = this.userSettingsService?.getSettingByName<string[]>(this.settingsKey);
    if (stored === undefined) {
      const defaults = [LogTypeFilter.Error.id, LogTypeFilter.Warning.id];
      this.applyTypeFilter(defaults);
      this.userSettingsService?.setSettingByName$(this.settingsKey, defaults);
    } else {
      this.applyTypeFilter(stored);
    }

    // Watch for user-driven filter changes (column-header filter UI, Clear button) and
    // mirror them into user settings. `skip(1)` drops the initial emission caused by our own
    // applyTypeFilter call above so we don't redundantly re-write the value we just read.
    this.filtersSubscription = this.logStreamRowService.getGridFilters$(this.gridId).pipe(
      skip(1)
    ).subscribe(filters => {
      const values = extractTypeFilterValues(filters);
      this.userSettingsService?.setSettingByName$(this.settingsKey, values);
    });
  }

  ngOnDestroy() {
    this.filtersSubscription?.unsubscribe();
    this.streamSub?.unsubscribe();
  }

  // Push a Type-filter selection into the grid's NGXS state. An empty array clears the filter
  // group entirely (`setGridFilterByName$` with `undefined` value drops the named group, which
  // is what `mc-grid-select-filter` does internally when its selection is cleared).
  private applyTypeFilter(values: string[]) {
    if (values.length === 0) {
      this.logStreamRowService.setGridFilterByName$(this.gridId, GridFilterName.Select, undefined).subscribe();
      return;
    }
    this.logStreamRowService.setGridFilterByName$(this.gridId, GridFilterName.Select, {
      Id: GridFilterName.Select,
      Type: PageFilterGroupType.Select,
      FilterGroups: [
        {
          Id: TYPE_COLUMN_NAME,
          Type: PageFilterGroupType.Select,
          Operator: PageFilterOperator.Or,
          Filters: values.map(v => makeTypeFilter(v))
        }
      ]
    }).subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.streamUrl) {
      const prev = changes.streamUrl.previousValue as string | undefined;
      if (prev !== this.streamUrl) {
        this.streamComplete = false;
        this.resetEntries();
        this.hardReload();
      }
    }
  }

  // Tears down the current scan subscription and starts a fresh one for the current streamUrl.
  // The fetcher reads from `entries$` via `this` reference so it always sees the latest
  // subject after this reset.
  private resetEntries() {
    this.streamSub?.unsubscribe();
    this.entries$ = new ReplaySubject<LogStreamRow[]>(1);

    if (!this.streamUrl) {
      return;
    }

    this.streamSub = this.logStreamService.getStream$(this.streamUrl).pipe(
      scan((acc: LogStreamRow[], msg: LogStreamMessage) => {
        let entry: LogStreamRow | null = null;
        if (msg.kind === 'log') {
          entry = parseLogToLogStreamRow(msg.data);
        } else if (msg.kind === 'event') {
          entry = parseEventToLogStreamRow(msg.data);
        }
        if (!entry) {
          return acc;
        }
        // Stable Id per entry — used by NGXS to key items. Position in the accumulator mirrors
        // the position in the underlying Redis list since the SSE replays from offset 0 in order.
        entry.Id = acc.length;
        return [...acc, entry];
      }, [] as LogStreamRow[])
    ).subscribe({
      next: entries => this.entries$.next(entries),
      complete: () => this.entries$.complete()
    });
  }

  logMessageTypeLabel(logMessageType: ProcessLogMessageType): string {
    return logTypeFilterLabel(logMessageType);
  }

  logMessageTypeIcon(logMessageType: ProcessLogMessageType): string {
    return resolveLogMessageTypeIcon(logMessageType);
  }

  logMessageTypeColorClass(logMessageType: ProcessLogMessageType): string {
    return resolveLogMessageTypeColorClass(logMessageType);
  }

  openLogDialog(message: string) {
    this.dialog.open(LogDialogComponent, {
      ...LogDialogComponent.DialogConfig,
      data: { message }
    });
  }

  // Defer to the caller-supplied resolver for source-file router links. When no resolver is
  // bound (or it returns null), the File column renders the path as plain text.
  getSourceRouterLink(src: string, line?: number): any[] | null {
    if (!src || !this.sourceLinkResolver) { return null; }
    return this.sourceLinkResolver(src, line) ?? null;
  }

  // Display label for the File link — `path:line` when the row has a line number, otherwise just the path.
  sourceLabel(file: string, lineIndex: number | null): string {
    return lineIndex ? `${file}:${lineIndex}` : file;
  }

  // Pull the user's Type column selection out of the merged PageFilter. Returns `null` when no
  // type filter is active (grid shows everything), otherwise a Set of allowed `ProcessLogMessageType` values.
  // An empty selection (all options deselected) also returns null so the grid shows everything
  // rather than nothing — "no types selected" means "no restriction", not "match nothing".
  private extractTypeFilter(pageFilter: PageFilter): Set<ProcessLogMessageType> | null {
    const values = extractTypeFilterValuesFromGroup(pageFilter);
    if (values.length === 0) {
      return null;
    }
    const allowed = new Set<ProcessLogMessageType>();
    for (const v of values) {
      const enumValue = logTypeFilterIdToLogMessageType(v);
      if (enumValue !== null) {
        allowed.add(enumValue);
      }
    }
    return allowed.size > 0 ? allowed : null;
  }
}
